import { Component, OnInit, AfterViewInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import {
  UpdateRejectedMeteringPointCommand,
  MeteringPointDto,
  MeteringPointsService,
  MeteringPointType,
  MeteringPointSubType,
} from '@app/api/dmc';
import { logFormErrors } from '@app/core/utils/log-form-errors';
import { FormGroupType } from '@app/core/utils/formgroup-type';
import {
  MeteringpointFormInstallationComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-installation/meteringpoint-form-installation.component';
import {
  MeteringpointFormCreationDetailsComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-creation-details/meteringpoint-form-creation-details.component';
import {
  MeteringpointFormReadingBillingComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-reading-billing/meteringpoint-form-reading-billing.component';
import {
  MeteringpointFormProductionComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-production/meteringpoint-form-production.component';
import {
  MeteringpointFormAddressComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-address/meteringpoint-form-address.component';
import { isoToRealDate } from '@app/core/utils/iso-to-real-date';
import { FormConfig } from '@shared/interfaces/form-configs/form-config';
import { meteringPointTypeConfig } from '@views/meteringpoint/configs/meteringpointtype-config';
import { applyFormConfig } from '@app/core/utils/apply-form-config';
import { GuideCardComponent } from '@shared/components/guide-card/guide-card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { MeteringpointFormExchangeComponent } from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-exchange/meteringpoint-form-exchange.component';
import {
  AddressDto,
} from '@app/api/nts';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { LucideAngularModule } from 'lucide-angular';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import {
  MeteringpointFormMeterComponent
} from '@views/meteringpoint/meteringpoint-form-fragments/meteringpoint-form-meter/meteringpoint-form-meter.component';
import { NgClass } from '@angular/common';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import { ActivatedRoute } from '@angular/router';
import { createMeteringPointFormGroup } from '@shared/forms/create-meteringpoint.formgroup';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import {
  MeteringPointFormHelperService
} from '@views/meteringpoint/services/meteringpoint-form-helper.service';

@Component({
  selector: 'app-edit-rejected-meteringpoint-modal',
  imports: [
    TranslatePipe,
    LucideAngularModule,
    InputGroupComponent,
    LabelDirective,
    SelectDirective,
    ReactiveFormsModule,
    InputDirective,
    GuideCardComponent,
    MeteringpointFormCreationDetailsComponent,
    MeteringpointFormInstallationComponent,
    MeteringpointFormReadingBillingComponent,
    MeteringpointFormProductionComponent,
    MeteringpointFormMeterComponent,
    MeteringpointFormAddressComponent,
    MeteringpointFormExchangeComponent,
    NgClass
  ],
  templateUrl: './edit-rejected-meteringpoint-modal.component.html'
})

export class EditRejectedMeteringpointModalComponent implements OnInit, AfterViewInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public form: FormGroupType<UpdateRejectedMeteringPointCommand> = createMeteringPointFormGroup();

  public childMeteringPoint: boolean = false;

  public meteringPointTypeList: KeyValue[] = enumToKeyValue(MeteringPointType, { omitKeys: ['NotSpecified', 'SurplusProductionGroup'], prefixValue: 'enum.meteringpoint.meteringpointtype.' });
  public meteringPointSubtypeList: KeyValue[] = enumToKeyValue(MeteringPointSubType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.meteringpointsubtype.' });

  public completeGridAreaList: KeyValue[] = [];

  public meteringPointDTO!: MeteringPointDto;

  protected readonly MeteringPointType = MeteringPointType;
  protected readonly MeteringPointSubType = MeteringPointSubType;

  private ignoreConfig = false;

  public configs: Partial<Record<MeteringPointType, FormConfig<UpdateRejectedMeteringPointCommand>>> = meteringPointTypeConfig;

  constructor(
    private readonly meteringPointsService: MeteringPointsService,
    private readonly route: ActivatedRoute,
    private readonly formApiService: FormApiService,
    private readonly formHelper: MeteringPointFormHelperService,
  ) {
    this.formHelper.loadGridAreas().subscribe(areas => this.completeGridAreaList = areas);
  }

  public ngOnInit(): void {
    this.form.get('detailCharacteristics.meteringPointType')?.valueChanges.subscribe((type: MeteringPointType) => {
      if (this.ignoreConfig) {
        this.ignoreConfig = false;
        return;
      }

      if (type && this.configs[type]) {
        applyFormConfig<UpdateRejectedMeteringPointCommand>(this.form, this.configs[type]);
      }
    });

    this.formHelper.setupSubTypeHandling(this.form);
    this.formHelper.setupParentMeteringPointHandling(this.form, isChild => this.childMeteringPoint = isChild);
    this.formHelper.registerConnectionPointChange(this.form);

    if (this.meteringPointDTO) {
      this.form.patchValue(this.meteringPointDTO);
      if (this.meteringPointDTO.validFrom) {
        this.form.get('validFrom')?.setValue(isoToRealDate(this.meteringPointDTO.validFrom));
      }

      if (this.form.get('detailCharacteristics.meteringPointType')?.value === MeteringPointType.Exchange) {
        this.form.get('detailCharacteristics.toGridAreaId')?.setValue(this.meteringPointDTO.detailCharacteristics?.toGrid?.id);
        this.form.get('detailCharacteristics.fromGridAreaId')?.setValue(this.meteringPointDTO.detailCharacteristics?.fromGrid?.id);
      }

      if (this.meteringPointDTO.connectionPointIdentification) {
        this.formHelper.populateFromConnectionPointID(this.form, this.meteringPointDTO.connectionPointIdentification, true);
      }
    }
  }

  public ngAfterViewInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['meteringPointType']) {
        this.form.get('detailCharacteristics.meteringPointType')?.setValue(params['meteringPointType']);
      }

      if (params['connectionPointIdentification']) {
        this.formHelper.populateFromConnectionPointID(this.form, params['connectionPointIdentification']);
      }
    });
  }

  public addressChanged(address: AddressDto): void {
    if (address) {
      this.form.get('installationLocationAddress')?.patchValue(address);
    } else {
      this.form.get('installationLocationAddress')?.setValue({});
    }
  }

  public submit(): void {
    if (this.form.invalid) {
      logFormErrors(this.form);
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      return;
    }

    this.form.get('installationLocationAddress')?.enable();
    this.form.get('templateId')?.disable({ emitEvent: false });

    if (!this.meteringPointDTO?.existsInDataHub) {
      this.formApiService.submit(
        this.form,
        (formData: UpdateRejectedMeteringPointCommand) => this.meteringPointsService.meteringPointsRejectedPut(removeNullKeys(formData)),
        {
          closeModal: true,
          successMessage: 'meteringPoint.create.success'
        }
      );
    }
  }

  public openMeteringPointModal(): void {
    this.formHelper.openMeteringPointModal().subscribe((meteringPointId: string) => {
      this.form.get('parentMeteringPointIdentification')?.patchValue(meteringPointId);
    });
  }

  public openConnectionPointSearchModal(): void {
    this.formHelper.openConnectionPointSearchModal().subscribe((data) => {
      this.formHelper.populateAddressFormFromConnectionPoint(this.form, data);
    });
  }
}
